import type { ReactNode } from "react";
import type { LandRegionSrc } from "@/lib/api";
import { cn } from "@/lib/cn";
import type { EvidenceItem } from "./evidence";
import { fmtNum } from "./shared";

/* 城市概况 / 区县指标 / 逐年表共用的小工具 */

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

export function signedPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
}

export function lastKey(series: Record<string, number> | undefined): string | null {
  const keys = Object.keys(series ?? {}).sort();
  return keys[keys.length - 1] ?? null;
}

/** 点开看出处: 主来源取最新一年, 其余年份列在「全部来源」里。 */
export function srcEvidence(opts: {
  title: string;
  value: string;
  unit?: string;
  rows?: LandRegionSrc[] | null;
  basis?: string[];
  formula?: string;
}): EvidenceItem {
  const rows = (opts.rows ?? []).filter((r) => r.title || r.url);
  const main = rows.length ? rows[rows.length - 1] : null;
  const calibers = Array.from(new Set(rows.map((r) => r.caliber).filter(Boolean)));
  return {
    title: opts.title,
    value: opts.value,
    unit: opts.unit,
    formula: opts.formula,
    basis: [...(opts.basis ?? []), ...calibers.slice(0, 2)],
    source: main
      ? {
          label: main.title || hostOf(main.url),
          url: main.url,
          domain: hostOf(main.url),
          period: main.year ? `${main.year}年` : undefined,
          snippet: main.quote || undefined,
        }
      : null,
    links:
      rows.length > 1
        ? rows.map((r) => ({
            title: [r.year ? `${r.year}年` : "", r.sub, r.title || hostOf(r.url)].filter(Boolean).join(" "),
            url: r.url,
            domain: hostOf(r.url),
            fact: r.quote || (r.value != null ? `${fmtNum(r.value, 2)}${r.unit}` : ""),
          }))
        : undefined,
  };
}

export function ValueButton({
  value,
  sub,
  onOpen,
  align = "right",
  muted = false,
}: {
  value: ReactNode;
  sub?: ReactNode;
  onOpen?: () => void;
  align?: "left" | "right";
  muted?: boolean;
}): JSX.Element {
  const inner = (
    <>
      <span className={cn("block tabular-nums", muted ? "text-zinc-400" : "font-medium text-zinc-900")}>{value}</span>
      {sub ? <span className="block text-[11px] leading-4 text-zinc-400">{sub}</span> : null}
    </>
  );
  if (!onOpen) return <div className={align === "right" ? "text-right" : "text-left"}>{inner}</div>;
  return (
    <button
      type="button"
      onClick={onOpen}
      title="查看来源"
      className={cn(
        "block w-full rounded px-1 py-0.5 hover:bg-brand-50",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {inner}
    </button>
  );
}

export function seriesText(series: Record<string, number> | undefined, digits: number, scale = 1, suffix = ""): string {
  return Object.keys(series ?? {})
    .sort()
    .map((y) => `${y.slice(2)}年 ${fmtNum((series?.[y] ?? 0) / scale, digits)}${suffix}`)
    .join(" · ");
}
